/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import WorkspaceIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import ProjectsIcon from '@mui/icons-material/FolderOpen';
import {
  type APIPermissions,
  AppSystemPermissions,
  ScenarioAPIPermissions,
  ToolAPIPermissions,
} from '@poweralpha/common';
import { useAgentWorkspaceTabLabels } from '../agent/workspace/useAgentWorkspaceSync';
import { useProjectSimulateTabLabels } from '../project-simulate/window/useTabLabels';
import { MetallicDockIcon } from './components/MetallicDockIcon';
import { metallicGlyphs } from './metallicGlyphs';
import type { DesktopApp } from './types';

const servicePermissions = [
  ToolAPIPermissions.READ_ESSCOSTING,
  ToolAPIPermissions.READ_PVCOSTING,
  ToolAPIPermissions.READ_PVSIM,
  ToolAPIPermissions.WRITE_PLANTSIM,
];

export const serviceApps: DesktopApp[] = [
  {
    frameless: true,
    id: 'database',
    label: 'Database',
    defaultPath: '/db',
    routes: ['/db', '/components'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.database} />,
    permissions: [
      ...servicePermissions,
      ToolAPIPermissions.READ_HVCOSTING,
      ScenarioAPIPermissions.WRITE_APPROVAL_ORIGINATION,
    ],
  },
  {
    frameless: true,
    id: 'pvsim',
    label: 'PVSim',
    defaultPath: '/pv/pvsim/results',
    routes: ['/pv/pvsim', '/pv'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.pvsim} />,
    permissions: [ToolAPIPermissions.READ_PVSIM],
  },
  {
    frameless: true,
    id: 'pvcosting',
    label: 'PVCosting',
    defaultPath: '/pv/costing',
    routes: ['/pv/costing'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.pvcosting} />,
    permissions: [ToolAPIPermissions.READ_PVCOSTING],
  },
  {
    frameless: true,
    id: 'ess',
    label: 'ESS',
    defaultPath: '/ess/costing',
    routes: ['/ess'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.ess} />,
    permissions: [ToolAPIPermissions.READ_ESSCOSTING],
  },
  {
    frameless: true,
    id: 'plant',
    label: 'PlantSim',
    defaultPath: '/plant-v2',
    // PlantSim v1 pages open in the same window, so v1/v2 links and agent resources stay in it.
    // So do the old Net Benefit pages (`/plant-v2/net-benefit`): net benefit is part of sweeps.
    routes: ['/plant', '/plant-v2'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.plantsim} />,
    permissions: [ToolAPIPermissions.WRITE_PLANTSIM],
  },
  {
    frameless: true,
    id: 'simulate',
    label: 'Project Simulate',
    defaultPath: '/projects/simulate',
    // Runs, run reports and finance models of a project. The older batch, single-run results and
    // FIMO pages open here too and redirect to the matching page; without a project the window
    // asks for one (`/projects/simulate`).
    routes: [
      '/projects/:projectId/simulate',
      '/projects/simulate',
      '/projects/:projectId/batch',
      '/projects/:projectId/results',
      '/projects/:projectId/configurations',
      '/fimo',
    ],
    icon: <MetallicDockIcon glyph={metallicGlyphs.fimo} />,
    useTabLabels: useProjectSimulateTabLabels,
    tabLabelsAreFallback: true,
    permissions: [
      ...servicePermissions,
      ToolAPIPermissions.READ_FIMO,
      ScenarioAPIPermissions.WRITE_APPROVAL_ENGINEERING,
    ],
  },
  {
    frameless: true,
    id: 'bid-requests',
    label: 'Bid Requests',
    defaultPath: '/bid-requests',
    routes: ['/bid-requests', '/opportunities'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.bidRequests} />,
  },
  {
    frameless: true,
    id: 'land-scoring',
    label: 'Land Scoring',
    defaultPath: '/projects/land-scoring',
    // Without a project the window asks for one (`/projects/land-scoring`).
    routes: ['/projects/:projectId/land-scoring', '/projects/land-scoring'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.landScoring} />,
    permissions: [ToolAPIPermissions.READ_PVSIM, ToolAPIPermissions.READ_PVCOSTING],
  },
  {
    frameless: true,
    id: 'create-parcels',
    label: 'Create Parcels',
    defaultPath: '/projects/create-parcels',
    // Without a project the window asks for one (`/projects/create-parcels`).
    routes: ['/projects/:projectId/create-parcels', '/projects/create-parcels'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.createParcels} />,
    permissions: [ToolAPIPermissions.WRITE_PARCELS],
  },
  // Pages without a dock entry yet. They open when something navigates to them.
  {
    frameless: true,
    hidden: true,
    id: 'projects',
    label: 'Projects',
    defaultPath: '/projects',
    routes: ['/projects'],
    icon: <ProjectsIcon />,
  },
  {
    hidden: true,
    id: 'notifications',
    label: 'Notifications',
    defaultPath: '/notifications',
    routes: ['/notifications'],
    frameless: true,
    icon: <MetallicDockIcon glyph={metallicGlyphs.notifications} />,
  },
  {
    hidden: true,
    overDesktop: true,
    id: 'settings',
    label: 'Settings',
    defaultPath: '/settings/appearance',
    routes: ['/settings'],
    frameless: true,
    icon: <MetallicDockIcon glyph={metallicGlyphs.settings} />,
  },
  {
    hidden: true,
    frameless: true,
    id: 'system-issues',
    label: 'System Issues',
    defaultPath: '/admin/system-issues',
    routes: ['/admin/system-issues'],
    icon: <MetallicDockIcon glyph={metallicGlyphs.systemAlerts} />,
    permissions: [AppSystemPermissions.SYSTEM_ADMIN],
  },
  // Opens when an artifact is expanded out of the Agent chat; reopened from the Agent header,
  // so it never takes a dock slot (PA-4471).
  {
    dockless: true,
    fixedTabs: true,
    hidden: true,
    id: 'agent-workspace',
    label: 'Workspace',
    // Every tab is named after its conversation, even while another tab is shown.
    useTabLabels: useAgentWorkspaceTabLabels,
    defaultPath: '/agent/workspace',
    routes: ['/agent/workspace'],
    icon: <WorkspaceIcon />,
  },
];

export const hasAnyPermission = (
  permissions: APIPermissions[] | undefined,
  required: APIPermissions[] | undefined,
) => !required || required.some((permission) => permissions?.includes(permission));
